//
// Copyright © 2026 DCM Moguls Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

//
// DCM DOORS (dcm.7). Rail entries that come from branding.json instead of from the model.
//
// A door is a rail entry for something DCM runs next to Pory: a same-origin page shown inside
// the workbench (kind "iframe", e.g. /ventures/) or another DCM site opened in a new tab (kind
// "link", e.g. https://hq.dcmmg.com). Adding one is an edit to branding.json -- no model
// document, no workspace upgrade, no rebuild:
//
//     "doors": [
//       { "alias": "ventures", "label": "Ventures", "icon": "ventures", "url": "/ventures/",
//         "kind": "iframe", "modes": ["simple", "standard"], "roles": ["USER"] },
//       { "alias": "fronte", "label": "Inbox (Fronte)", "icon": "fronte", "url": "/fronte/",
//         "modes": ["simple", "standard"],
//         "onlyFor": { "roles": ["OWNER"], "emails": ["ninna.s@dcmmoguls.com"] } }
//     ]
//
// Who sees a door -- still a UI fence, never a data fence (see ./appAccess.ts):
//   - `modes`: the door's alias is added to those modes' allow-lists ("*" modes see it anyway);
//   - `roles`: the alias is added to those roles' `appsByRole` lists (for people with no mode);
//   - everything else about the fence is unchanged: Owners and unconstrained roles see every
//     door, a picked "View as" mode is honoured, and so on;
//   - `onlyFor` (optional) is a hard audience on top: the person must hold one of its roles or
//     one of its e-mails, whatever their mode -- Owners included unless OWNER is listed.
//     Use it for a door whose page refuses most people anyway (Fronte), so nobody is shown a
//     door that only leads to "not allowed".
//
// Deliberately zero runtime imports, like ./appAccess.ts, so it is unit-testable standalone.
//

import type { AppAccessSubject, ApplicationModes, ApplicationsByRole } from './appAccess'

/** @public */
export type DoorKind = 'iframe' | 'link'

/** A door after validation. @public */
export interface Door {
  alias: string
  label: string
  /** Icon name, e.g. 'ventures'; resolved to an asset by the front. */
  icon: string
  url: string
  kind: DoorKind
  modes: string[]
  roles: string[]
  onlyFor?: { roles: string[], emails: string[] }
  order: number
}

/** Aliases a door may never take: workbench path segments and the Pory model applications. */
const RESERVED_ALIASES = ['workbench', 'login', 'guest', 'setting', 'inbox', 'notification', 'ditz', 'brain']

const ALIAS_RE = /^[a-z][a-z0-9-]{0,39}$/
const ICON_RE = /^[a-z][a-z0-9-]{0,39}$/i

function stringList (value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((it): it is string => typeof it === 'string' && it.trim() !== '').map((it) => it.trim())
}

/**
 * A same-origin absolute path: '/x/...'. Not '//host' (protocol-relative), no backslash (which
 * browsers read as a slash), no control characters or whitespace.
 *
 * @public
 */
export function isSameOriginPath (url: string): boolean {
  if (!url.startsWith('/') || url.startsWith('//')) return false
  // eslint-disable-next-line no-control-regex
  return !/[\\\s\u0000-\u001f]/.test(url)
}

/** An https URL with a host, nothing else (no javascript:, data:, http:). @public */
export function isHttpsUrl (url: string): boolean {
  // eslint-disable-next-line no-control-regex
  if (/[\\\s\u0000-\u001f]/.test(url)) return false
  return /^https:\/\/[a-z0-9.-]+(:[0-9]+)?(\/|$|\?|#)/i.test(url)
}

/**
 * Validate the raw `doors` value from branding.json. Malformed entries are dropped one by one
 * (a typo must not take the other doors away); so are duplicates (first wins) and reserved or
 * otherwise taken aliases.
 *
 *  - kind: 'iframe' (default) needs a same-origin path -- other DCM sites refuse to be framed,
 *    and a cross-origin frame would not carry Pory's session anyway;
 *  - kind: 'link' (alias 'link-out') takes an https URL or a same-origin path; opens a new tab.
 *
 * @public
 */
export function parseDoors (raw: unknown, takenAliases: Iterable<string> = []): Door[] {
  if (!Array.isArray(raw)) return []
  const taken = new Set<string>([...RESERVED_ALIASES, ...takenAliases])
  const out: Door[] = []
  raw.forEach((entry: unknown, index: number) => {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return
    const e = entry as Record<string, unknown>
    const alias = typeof e.alias === 'string' ? e.alias.trim() : ''
    if (!ALIAS_RE.test(alias) || taken.has(alias)) return
    const label = typeof e.label === 'string' ? e.label.trim() : ''
    if (label === '' || label.length > 60) return
    const kindRaw = typeof e.kind === 'string' ? e.kind.trim().toLowerCase() : 'iframe'
    const kind: DoorKind | undefined =
      kindRaw === 'iframe' ? 'iframe' : kindRaw === 'link' || kindRaw === 'link-out' ? 'link' : undefined
    if (kind === undefined) return
    const url = typeof e.url === 'string' ? e.url.trim() : ''
    if (kind === 'iframe' && !isSameOriginPath(url)) return
    if (kind === 'link' && !isSameOriginPath(url) && !isHttpsUrl(url)) return
    const icon = typeof e.icon === 'string' && ICON_RE.test(e.icon.trim()) ? e.icon.trim().toLowerCase() : 'door'
    const order = typeof e.order === 'number' && Number.isFinite(e.order) ? e.order : 9100 + index
    let onlyFor: Door['onlyFor']
    if (e.onlyFor !== undefined) {
      const o = e.onlyFor
      if (o === null || typeof o !== 'object' || Array.isArray(o)) return // malformed audience: fail closed
      const rec = o as Record<string, unknown>
      onlyFor = { roles: stringList(rec.roles), emails: stringList(rec.emails) }
    }
    taken.add(alias)
    out.push({ alias, label, icon, url, kind, modes: stringList(e.modes), roles: stringList(e.roles), onlyFor, order })
  })
  return out
}

/**
 * Is this person in the door's hard audience? No `onlyFor` = everybody. An `onlyFor` naming
 * nobody = nobody. Roles and e-mails match case-insensitively.
 *
 * @public
 */
export function isDoorForSubject (door: Pick<Door, 'onlyFor'>, subject: AppAccessSubject | undefined): boolean {
  const only = door.onlyFor
  if (only === undefined) return true
  const role = subject?.role
  if (typeof role === 'string' && only.roles.some((r) => r.toUpperCase() === role.toUpperCase())) return true
  const emails = Array.isArray(subject?.emails) ? subject?.emails ?? [] : []
  const wanted = new Set(only.emails.map((it) => it.toLowerCase()))
  return emails.some((it) => typeof it === 'string' && wanted.has(it.trim().toLowerCase()))
}

function addAlias (list: string[], alias: string): string[] {
  return list.includes(alias) ? list : [...list, alias]
}

/**
 * The role table with each door's alias added to the roles it names. A role the table does not
 * constrain stays unconstrained (it sees the door already); a door never creates a role entry,
 * since that would constrain a role that was not. Never mutates its input.
 *
 * @public
 */
export function mergeDoorsIntoRoleTable (
  table: ApplicationsByRole | undefined,
  doors: Door[]
): ApplicationsByRole | undefined {
  if (table === undefined || table === null || typeof table !== 'object') return table
  const out: ApplicationsByRole = { ...table }
  for (const door of doors) {
    for (const role of door.roles) {
      const key = Object.keys(out).find((k) => k.toUpperCase() === role.toUpperCase())
      if (key === undefined || !Array.isArray(out[key])) continue
      out[key] = addAlias(out[key], door.alias)
    }
  }
  return out
}

/**
 * The modes table with each door's alias added to the modes it names. A '*' mode is left as it
 * is (it shows every door already); an unknown mode name is ignored. Never mutates its input.
 *
 * @public
 */
export function mergeDoorsIntoModes (modes: ApplicationModes | undefined, doors: Door[]): ApplicationModes | undefined {
  const table = modes?.modes
  if (modes === undefined || table === undefined || table === null || typeof table !== 'object') return modes
  const merged: Record<string, string[] | '*'> = { ...table }
  for (const door of doors) {
    for (const mode of door.modes) {
      const key = Object.keys(merged).find((k) => k.trim().toLowerCase() === mode.toLowerCase())
      if (key === undefined) continue
      const entry = merged[key]
      if (!Array.isArray(entry)) continue
      merged[key] = addAlias(entry, door.alias)
    }
  }
  return { ...modes, modes: merged }
}

/**
 * The door a workbench tab at `alias` shows: an iframe door among `shownDoors` (the doors the
 * account is shown -- pass getDoorsForCurrentAccount(), never getDoors(), so the fence holds). A
 * link door has no page in the workbench, so it never names a tab.
 *
 * @public
 */
export function findTabDoor (shownDoors: Door[], alias: string | undefined): Door | undefined {
  if (alias === undefined) return undefined
  return shownDoors.find((d) => d.kind === 'iframe' && d.alias === alias)
}
